/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { AdministrationOffice } from "@/models/api/administration/offices/AdministrationOffice";
import {
  getOfficeProfilesByOfficeId,
  getAdministrationOffices,
  getContractorDetails,
  getSettingsByOffice,
  postUpdateSettingsByOffice,
  getDocumentTemplates,
  getCashRegisters,
  removeCashRegister,
  addCashRegister,
  editCashRegister,
  postEditDocumentTemplates,
  postAddDocumentTemplates,
  getDocumentTemplateTypes,
  getCashiers,
  getCashiersOfficeProfileOwners,
  addProfileOwner,
  removeProfileOwner,
  getTraders,
  getTradersOfficeProfileOwners,
  uploadSignatureCertificate,
  uploadSignatureImage,
  removeSignatureCertificate,
  removeSignatureImage,
  getSignaturesInfo,
  getOfficeProfileList,
  editOfficeProfile,
  addOfficePofile,
  getOfficeProfileDetails,
  getOfficeDetails,
  updateOfficeDetails,
  postUpdateImage,
  getDefaultInvoiceSendSettings,
  saveDefaultInvoiceSendSetting,
  turnOnSendRemindersForEveryone,
} from "@/requests/administration/administrationOffices";

import {
  getFormOfPaymentByOfficeProfileId,
  assignFormOfPaymentToOfficeProfile,
  unassignFormOfPaymentToOfficeProfile,
  setValueCanIssueReceipt,
  setValueCanIssueReceiptIndividualContractor,
} from "@/requests/administration/administrationPayments";
import { ContractorDataModel } from "@/models/api/administration/offices/ContractorDataModel";
import { OfficeSettingsRest } from "@/models/api/administration/offices/OfficeSettingsRest";
import { DocumentTemplate, DocumentTemplateAddEdit } from "@/models/api/administration/offices/DocumentTemplate";
import { OfficeProfileResult } from "@/models/api/administration/servicesAndAttributes/ServiceTypeExportConfiguration";
import { OfficeCashBox } from "@/models/api/administration/offices/OfficeCashBox";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { bold } from "@/filters/bold";
import { OfficeDocumentType } from "@/models/api/administration/offices/OfficeDocumentType";
import { OfficeFormOfPayment } from "@/models/api/administration/offices/OfficeFormOfPayment";
import { OfficeOwner, OfficeProfileOwner } from "@/models/api/administration/offices/OfficeOwner";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import { OWNER_TYPES } from "@/static/ownerTypes";
import { Report } from "@/models/api/report/Report";
import {
  getReportTypes,
  assignReportTypeToOfficeProfile,
  unassignReportTypeToOfficeProfile,
} from "@/requests/report/report";
import { FORM_OF_PAYMENTS } from "@/static/formOfPayments";

import { OfficeSignature } from "@/models/api/administration/offices/OfficeSignature";
import {
  OfficeDetails,
  OfficeProfileDetails,
  OfficeProfileInfo,
} from "@/models/api/administration/offices/OfficeProfile";
import { DefaultInvoiceSendSettings } from "@/models/api/administration/offices/DefaultInvoiceSendSettings";

export interface AdministrationOfficesStateInterface {
  isLoading: boolean;
  offices: Array<AdministrationOffice>;
  selectedOffice: Array<AdministrationOffice>;
  selectedOfficeId: string;
  officeProfiles: Array<OfficeProfileResult>;
  selectedOfficeProfile: OfficeProfileResult | null;
  address: ContractorDataModel;
  settings: OfficeSettingsRest;
  defaultInvoiceSendSettings: DefaultInvoiceSendSettings;
  documentRegisters: Array<DocumentTemplate>;
  documentTypes: Array<OfficeDocumentType>;
  cashRegisters: Array<OfficeCashBox>;
  isCashRegisterDialog: boolean;
  isOfficeProfileDialog: boolean;
  isOfficeDialog: boolean;
  formOfPayments: Array<OfficeFormOfPayment>;
  cashiers: Array<OfficeOwner>;
  cashiersProfileOwner: Array<OfficeProfileOwner>;
  traders: Array<OfficeOwner>;
  tradersProfileOwner: Array<OfficeProfileOwner>;
  reportTypes: Array<Report>;
  signature: OfficeSignature | null;
  officeProfileList: Array<OfficeProfileInfo>;
  officeProfileDetails: OfficeProfileDetails | null;
  officeDetails: OfficeDetails | null;
}

export const AdministrationOfficesState: AdministrationOfficesStateInterface = {
  isLoading: false,
  offices: [],
  selectedOffice: [],
  selectedOfficeId: "",
  officeProfiles: [],
  selectedOfficeProfile: null,
  address: new ContractorDataModel(),
  settings: new OfficeSettingsRest(),
  defaultInvoiceSendSettings: new DefaultInvoiceSendSettings(),
  documentRegisters: [],
  documentTypes: [],
  cashRegisters: [],
  isCashRegisterDialog: false,
  isOfficeProfileDialog: false,
  isOfficeDialog: false,
  formOfPayments: [],
  cashiers: [],
  cashiersProfileOwner: [],
  traders: [],
  tradersProfileOwner: [],
  reportTypes: [],
  signature: null,
  officeProfileList: [],
  officeProfileDetails: null,
  officeDetails: null,
};

const AdministrationOfficesModule = defineModule({
  state: AdministrationOfficesState as AdministrationOfficesStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setOffices(state, param: Array<AdministrationOffice>) {
      state.offices = param;
    },
    setSelectedOffice(state, param: AdministrationOffice) {
      state.selectedOffice = [];
      state.selectedOfficeId = param.id;
      state.selectedOffice.push(param);
    },
    setOfficeProfiles(state, param: Array<OfficeProfileResult>) {
      state.officeProfiles = param;
    },
    setSelectedOfficeProfile(state, index: number) {
      state.selectedOfficeProfile = state.officeProfiles[index];
    },
    setAddress(state, param: ContractorDataModel) {
      state.address = param;
    },
    setSettings(state, param: OfficeSettingsRest) {
      state.settings = param;
    },
    setDefaultInvoiceSendSettings(state, param: DefaultInvoiceSendSettings) {
      state.defaultInvoiceSendSettings = param;
    },
    setDocumentRegisters(state, param: Array<DocumentTemplate>) {
      state.documentRegisters = param;
    },
    setDocumentTypes(state, param: Array<OfficeDocumentType>) {
      state.documentTypes = param;
    },
    setCashRegisters(state, param: Array<OfficeCashBox>) {
      state.cashRegisters = param;
    },
    setIsCashRegisterDialog(state, param: boolean) {
      state.isCashRegisterDialog = param;
    },
    setIsOfficeProfileDialog(state, param: boolean) {
      state.isOfficeProfileDialog = param;
    },
    setIsOfficeDialog(state, param: boolean) {
      state.isOfficeDialog = param;
    },
    setFormOfPayments(state, param: Array<OfficeFormOfPayment>) {
      state.formOfPayments = param;
    },
    setCashiers(state, param: Array<OfficeOwner>) {
      state.cashiers = param;
    },
    setCashiersProfileOwner(state, param: Array<OfficeProfileOwner>) {
      state.cashiersProfileOwner = param;
    },
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setTraders(state, param: Array<OfficeOwner>) {
      state.traders = param;
    },
    setTradersProfileOwner(state, param: Array<OfficeProfileOwner>) {
      state.tradersProfileOwner = param;
    },
    setReportTypes(state, param: Array<Report>) {
      state.reportTypes = param;
    },
    setSignatureExist(state, param: OfficeSignature) {
      state.signature = param;
    },
    setOfficeProfileList(state, param: Array<OfficeProfileInfo>) {
      state.officeProfileList = param;
    },
    setOfficeProfileDetails(state, param: OfficeProfileDetails | null) {
      state.officeProfileDetails = param;
    },
    setOfficeDetails(state, param: OfficeDetails | null) {
      state.officeDetails = param;
    },
  },
  actions: {
    async getOfficeProfiles(context) {
      const { commit, state } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const officeProfiles = await getOfficeProfilesByOfficeId(officeId);
      commit.setOfficeProfiles(officeProfiles);
    },
    async downloadOffices(context) {
      const { commit } = _moduleActionContext(context);
      const offices = await getAdministrationOffices();
      commit.setOffices(offices);
      commit.setSelectedOffice(offices[0]);
    },
    async getOfficeDetails(context) {
      const { state, commit } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const officeDetails = await getOfficeDetails(officeId);
      commit.setOfficeDetails(officeDetails);
    },
    async editOfficeDetails(context, officeDetails: OfficeDetails) {
      const { state, commit, dispatch, rootDispatch } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      await updateOfficeDetails(officeId, officeDetails);
      await commit.setIsOfficeDialog(false);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
      await dispatch.downloadOffices();
    },
    async getAddress(context) {
      const { commit, state } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const address = await getContractorDetails(officeId);
      commit.setAddress(address);
    },
    async getSettingsByOffice(context) {
      const { commit, state } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const settings = await getSettingsByOffice(officeId);
      commit.setSettings(settings);
    },
    async getDefaultInvoiceSendSettingsForOffice(context) {
      const { commit, state } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const result = await getDefaultInvoiceSendSettings(officeId);
      commit.setDefaultInvoiceSendSettings(result);
    },
    async postUpdateSettingsByOffice(context) {
      const { state, rootDispatch } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const settings = state.settings;
      await postUpdateSettingsByOffice(officeId, settings);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async saveDefaultInvoiceSendSettings(context) {
      const { state, rootDispatch } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const settings = state.defaultInvoiceSendSettings;
      await saveDefaultInvoiceSendSetting(officeId, settings);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async updateImage(context, params: { file: File; type: number }) {
      const { state, rootDispatch, dispatch } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const response = await postUpdateImage(params.file, officeId, params.type);
      if (response.status === 200) {
        dispatch.getSettingsByOffice();
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async turnOnSendRemindersForEveryone(context) {
      const { state, rootDispatch } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      await turnOnSendRemindersForEveryone(officeId);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async getDocumentRegisters(context) {
      const { state, commit } = _moduleActionContext(context);
      const officeProfile = state.selectedOfficeProfile;
      if (officeProfile != null) {
        const documentRegisters = await getDocumentTemplates(officeProfile.id);
        commit.setDocumentRegisters(documentRegisters);
      }
    },
    async getDocumentTypes(context) {
      const { commit } = _moduleActionContext(context);
      const documentTypes = await getDocumentTemplateTypes();
      commit.setDocumentTypes(documentTypes);
    },
    async addAttributeTemplate(context, template: DocumentTemplateAddEdit) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const officeProfile = state.selectedOfficeProfile;
      if (officeProfile != null) {
        await postAddDocumentTemplates(officeProfile.id, template);
        dispatch.getDocumentRegisters();
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      } else
        rootDispatch.popup.addSnackbar(
          new SnackbarModel(filterCapitalize(i18n.t("selectOfficeProfile").toString()), "error")
        );
    },
    async editAttributeTemplate(context, template: DocumentTemplateAddEdit) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const officeProfile = state.selectedOfficeProfile;
      if (officeProfile != null) {
        await postEditDocumentTemplates(officeProfile.id, template);
        dispatch.getDocumentRegisters();
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      } else
        rootDispatch.popup.addSnackbar(
          new SnackbarModel(filterCapitalize(i18n.t("selectOfficeProfile").toString()), "error")
        );
    },
    async getCashRegisters(context) {
      const { state, commit } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const cashRegisters = await getCashRegisters(officeId);
      commit.setCashRegisters(cashRegisters);
    },
    async addCashRegister(context, item: OfficeCashBox) {
      const { state, commit, dispatch, rootDispatch } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      item.officeId = officeId;
      await addCashRegister(item);
      dispatch.getCashRegisters();
      commit.setIsCashRegisterDialog(false);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async removeCashRegister(context, item: OfficeCashBox) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      const isRemoveConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(
            i18n.t("doYouReallyWantToDoActionCashRegister", {
              action: bold(i18n.t("toRemove")),
              cashBoxName: bold(item.name),
            })
          ),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "550px"
        )
      )) as boolean;
      if (isRemoveConfirmed) {
        await removeCashRegister(item.id);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        dispatch.getCashRegisters();
      }
    },
    async editCashRegister(context, item: OfficeCashBox) {
      const { commit, dispatch, rootDispatch } = _moduleActionContext(context);
      await editCashRegister(item);
      dispatch.getCashRegisters();
      commit.setIsCashRegisterDialog(false);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async getFormOfPaymentsByOfficeProfile(context) {
      const { state, commit, rootDispatch } = _moduleActionContext(context);
      const officeProfile = state.selectedOfficeProfile;
      if (officeProfile != null) {
        const formOfPayment = await getFormOfPaymentByOfficeProfileId(officeProfile.id);
        commit.setFormOfPayments(formOfPayment);
      } else
        rootDispatch.popup.addSnackbar(
          new SnackbarModel(filterCapitalize(i18n.t("selectOfficeProfile").toString()), "error")
        );
    },
    async assignFormOfPaymentToOfficeProfile(context, paymentMethodId: number) {
      const { state, rootDispatch } = _moduleActionContext(context);
      const officeProfile = state.selectedOfficeProfile;
      if (officeProfile != null) {
        await assignFormOfPaymentToOfficeProfile(officeProfile.id, paymentMethodId);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async unassignFormOfPaymentToOfficeProfile(context, paymentMethodId: number) {
      const { state, rootDispatch } = _moduleActionContext(context);
      const officeProfile = state.selectedOfficeProfile;
      if (officeProfile != null) {
        await unassignFormOfPaymentToOfficeProfile(officeProfile.id, paymentMethodId);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async setValueCanIssueReceipt(context, param: { formOfPayment: FORM_OF_PAYMENTS; value: boolean }) {
      const { rootDispatch } = _moduleActionContext(context);
      await setValueCanIssueReceipt(param.formOfPayment, param.value);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async setValueCanIssueReceiptIndividualContractor(
      context,
      param: { formOfPayment: FORM_OF_PAYMENTS; value: boolean }
    ) {
      const { rootDispatch } = _moduleActionContext(context);
      await setValueCanIssueReceiptIndividualContractor(param.formOfPayment, param.value);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async getOwner(context, ownerType: OWNER_TYPES) {
      const { state, commit } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const officeProfile = state.selectedOfficeProfile;
      if (officeProfile != null) {
        commit.setIsLoading(true);
        if (ownerType == OWNER_TYPES.Cashier) {
          const cashiers = await getCashiers(officeId, officeProfile.id);
          const cashiersProfileOwner = await getCashiersOfficeProfileOwners(officeProfile.id);
          commit.setCashiers(cashiers);
          commit.setCashiersProfileOwner(cashiersProfileOwner);
        }
        if ((ownerType = OWNER_TYPES.Trader)) {
          const traders = await getTraders(officeId, officeProfile.id);
          const tradersProfileOwner = await getTradersOfficeProfileOwners(officeProfile.id);
          commit.setTraders(traders);
          commit.setTradersProfileOwner(tradersProfileOwner);
        }
        commit.setIsLoading(false);
      }
    },
    async getOfficeProfileOwners(context, ownerType: OWNER_TYPES) {
      const { state, commit } = _moduleActionContext(context);
      const officeProfile = state.selectedOfficeProfile;
      if (officeProfile != null) {
        commit.setIsLoading(true);
        if (ownerType == OWNER_TYPES.Cashier) {
          const cashiersProfileOwner = await getCashiersOfficeProfileOwners(officeProfile.id);
          commit.setCashiersProfileOwner(cashiersProfileOwner);
        }
        if ((ownerType = OWNER_TYPES.Trader)) {
          const tradersProfileOwner = await getTradersOfficeProfileOwners(officeProfile.id);
          commit.setTradersProfileOwner(tradersProfileOwner);
        }
      }
      commit.setIsLoading(false);
    },
    async addProfileOwner(context, param: { ownerId: number; ownerType: OWNER_TYPES }) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const officeProfile = state.selectedOfficeProfile;
      if (officeProfile != null) {
        await addProfileOwner(officeProfile.id, param.ownerId);
        await dispatch.getOwner(param.ownerType);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async removeProfileOwner(context, param: { ownerId: number; ownerType: OWNER_TYPES }) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const officeProfile = state.selectedOfficeProfile;
      if (officeProfile != null) {
        await removeProfileOwner(officeProfile.id, param.ownerId);
        await dispatch.getOwner(param.ownerType);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async getReportTypes(context) {
      const { state, commit } = _moduleActionContext(context);
      const officeProfile = state.selectedOfficeProfile;
      if (officeProfile != null) {
        const reportTypes = await getReportTypes(officeProfile.id);
        commit.setReportTypes(reportTypes);
      }
    },
    async assignReportTypeToOfficeProfile(context, itemId: number) {
      const { state, rootDispatch } = _moduleActionContext(context);
      const officeProfile = state.selectedOfficeProfile;
      if (officeProfile != null) {
        await assignReportTypeToOfficeProfile(officeProfile.id, itemId);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async unassignReportTypeToOfficeProfile(context, itemId: number) {
      const { state, rootDispatch } = _moduleActionContext(context);
      const officeProfile = state.selectedOfficeProfile;
      if (officeProfile != null) {
        await unassignReportTypeToOfficeProfile(officeProfile.id, itemId);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async getSignaturesInfo(context) {
      const { state, commit } = _moduleActionContext(context);
      const names = await getSignaturesInfo(state.selectedOfficeId);
      commit.setSignatureExist(names);
    },
    async uploadSignatureCertificate(context, file: File) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const response = await uploadSignatureCertificate(file, officeId);
      if (response.status === 200) {
        dispatch.getSignaturesInfo();
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async uploadSignatureImage(context, file: File) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const response = await uploadSignatureImage(file, officeId);
      if (response.status === 200) {
        dispatch.getSignaturesInfo();
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async removeSignatureCertificate(context) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      await removeSignatureCertificate(officeId);
      dispatch.getSignaturesInfo();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async removeSignatureImage(context) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      await removeSignatureImage(officeId);
      dispatch.getSignaturesInfo();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async getOfficeProfileList(context) {
      const { state, commit } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const result = await getOfficeProfileList(officeId);
      commit.setOfficeProfileList(result);
    },
    async getOfficeProfileDetails(context, officeProfileId: number) {
      const { state, commit } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      const result = await getOfficeProfileDetails(officeId, officeProfileId);
      commit.setOfficeProfileDetails(result);
    },
    async addOfficeProfile(context, item: OfficeProfileDetails) {
      const { state, commit, dispatch, rootDispatch } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      await addOfficePofile(officeId, item);
      dispatch.getOfficeProfileList();
      commit.setIsOfficeProfileDialog(false);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async editOfficeProfile(context, item: OfficeProfileDetails) {
      const { state, commit, dispatch, rootDispatch } = _moduleActionContext(context);
      const officeId = state.selectedOfficeId;
      await editOfficeProfile(officeId, item.id, item);
      dispatch.getOfficeProfileList();
      commit.setIsOfficeProfileDialog(false);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
  },
});

export default AdministrationOfficesModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationOfficesModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationOfficesModule);
